import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import dayjs from 'dayjs'
import { Formik } from 'formik'
import React, { useCallback, useEffect, useState } from 'react'

import {
  fetchDeclinedMark,
  fetchInteractionHistory,
  saveDeclinedMark,
  updateInteractionHistory
} from 'api'
import InteractionFields, {
  INTERACTION_FOLLOW_UP_STATUSES,
  isInteractionFollowUpAnswered,
  requiredFollowUpDateProblem
} from 'app/components/InteractionFields'
import { showToast } from 'app/helpers'
import {
  INTERACTION_TEMPLATES,
  getMissingTemplateFields
} from 'app/pages/Communication/InteractionHistory/templates'

import { dayMonthYear } from './monthHelpers'

// Inline "log + read" surface for the chase / enquiry rows. Opens as a right
// drawer so the board stays visible behind it (user decision 2026-07-18):
// the team logs a follow-up AND reads the parent's past conversations without
// leaving the Revenue Goals page. Everything reuses the existing CRM
// interaction endpoints (POST/GET /crm/interaction/) — no new backend.
//
// The form is `InteractionFields`, the SAME component the parent dashboard
// renders (user 2026-08-21). It used to be a hand-rolled lighter copy: a single
// topic instead of many, plain text instead of the WYSIWYG, no voice capture,
// and no follow-up question — so a callback promised on a Grow & Retain call
// was silently dropped and never reached the Follow-ups tab or the "Clear
// callbacks due" target. That component needs a Formik context, hence the
// <Formik> wrapper around the log section.
const useStyles = makeStyles(() => ({
  drawerPaper: { width: 460, maxWidth: '92vw' },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px',
    borderBottom: '1px solid #eee'
  },
  section: { padding: '14px 18px' },
  // InteractionFields lays its controls out as plain siblings (it is normally
  // hosted by BaseForm, which supplies the stacking) — give them the same
  // rhythm here instead of letting them collapse into each other.
  form: { display: 'flex', flexDirection: 'column', gap: 12, marginTop: 10 },
  topicChip: {
    borderRadius: 100,
    backgroundColor: '#ffe1d9',
    color: '#f0491e',
    padding: '1px 12px',
    fontWeight: 700,
    fontSize: 12,
    display: 'inline-block',
    marginRight: 6,
    marginBottom: 4
  },
  histItem: { padding: '12px 0', borderBottom: '1px solid #f0f0f0' },
  muted: { color: '#9e9e9e' },
  summaryHtml: { fontSize: 13, marginTop: 4, wordBreak: 'break-word' }
}))

// The WYSIWYG stores HTML, so "did they write anything" has to look past the
// tags — '<p></p>' and '<p><br></p>' are an empty note, not a summary.
const hasText = (html) =>
  (html || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim().length > 0

// `allowDeclinedMark` — only the Interactions Audit surfaces pass it: the
// "Declined subscription" tick-box is about the non-member chase cycle, and
// showing it on Revenue Goals' member-facing drawers would invite marks that
// mean something else. The environment gate still applies on top.
const InteractionDrawer = ({
  parent,
  onClose,
  onLogged,
  allowDeclinedMark
}) => {
  const classes = useStyles()
  const open = Boolean(parent)
  const parentUuid = parent?.uuid

  const [history, setHistory] = useState([])
  const [loadingHistory, setLoadingHistory] = useState(false)

  // The "Declined subscription" mark — hidden entirely unless the environment
  // gate is on (declined.enabled). The tick rides the Save on purpose: the
  // note logged alongside is the evidence the parent actually said it.
  const [declined, setDeclined] = useState({
    enabled: false,
    active: false,
    expires_on: null,
    days: 60
  })
  const [declinedChecked, setDeclinedChecked] = useState(false)

  const today = dayjs().format('YYYY-MM-DD')

  const loadHistory = useCallback(async (uuid) => {
    if (!uuid) return
    setLoadingHistory(true)
    try {
      const res = await fetchInteractionHistory({ queries: { parent: uuid } })
      setHistory(res?.results || [])
    } catch (error) {
      setHistory([])
      showToast('Could not load this parent’s interaction history', 'error')
    } finally {
      setLoadingHistory(false)
    }
  }, [])

  useEffect(() => {
    if (!open) return
    loadHistory(parentUuid)
    // Non-fatal: a failed read just leaves the box hidden for this open.
    setDeclined({ enabled: false, active: false, expires_on: null, days: 60 })
    setDeclinedChecked(false)
    if (allowDeclinedMark) {
      fetchDeclinedMark(parentUuid)
        .then((d) => {
          setDeclined(d || { enabled: false })
          setDeclinedChecked(Boolean(d?.active))
        })
        .catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentUuid, open])

  // A fresh log: today, Connected (the overwhelmingly common case, and what the
  // old drawer defaulted to), one topic row prefilled from the row that opened
  // the drawer, follow-up UNANSWERED so the team has to answer it.
  const initialValues = {
    interaction_date: today,
    connected_status: 'Connected',
    interaction_mode: '',
    interaction_topics: [
      { topic: parent?.topic || '', summary: '', structured_data: null }
    ],
    follow_up_date: ''
  }

  // The same mandatory rules the parent dashboard enforces before it saves.
  // Returned as a message so the team is told WHICH field is missing rather
  // than staring at a dead button.
  const firstProblem = (values) => {
    const isConnected = values.connected_status === 'Connected'
    if (!values.interaction_date) return 'Pick the interaction date'
    if (!values.connected_status) return 'Pick the outcome'
    if (isConnected && !values.interaction_mode) {
      return 'Pick the interaction channel'
    }
    // The topic is asked for on EVERY outcome here, DNP included — chase.py
    // reads it to decide a parent is unreachable. The summary (and its
    // templates) belong to a Connected call only.
    const rows = values.interaction_topics || []
    if (!rows.length) return 'Add at least one topic'
    for (const row of rows) {
      if (!row.topic) return 'Pick a topic'
      if (!isConnected) continue
      const template = INTERACTION_TEMPLATES[row.topic]
      if (template) {
        const missing = getMissingTemplateFields(
          template,
          row.structured_data || {}
        )
        if (missing.length) return `${row.topic}: ${missing.join(', ')}`
      } else if (!hasText(row.summary)) {
        return `${row.topic}: add a summary`
      }
    }
    // Recharge "Will recharge" = Yes / Undecided needs a real follow-up date
    // (user 2026-09-24) — checked before the generic follow-up message, which
    // would offer "No".
    const followUpDateProblem = requiredFollowUpDateProblem(values)
    if (followUpDateProblem) return followUpDateProblem
    if (
      INTERACTION_FOLLOW_UP_STATUSES.includes(values.connected_status) &&
      !isInteractionFollowUpAnswered(values.follow_up_date)
    ) {
      return 'Answer whether a follow-up is required (and pick a date)'
    }
    return null
  }

  const handleSubmit = async (values, { resetForm }) => {
    const problem = firstProblem(values)
    if (problem) {
      showToast(problem, 'error')
      return
    }
    const isConnected = values.connected_status === 'Connected'
    try {
      await updateInteractionHistory({
        interaction_date: values.interaction_date,
        connected_status: values.connected_status,
        parent: parentUuid,
        interaction_mode: isConnected ? values.interaction_mode : null,
        interaction_topics: (values.interaction_topics || [])
          .filter((row) => row.topic)
          .map((row) => ({
            topic: row.topic,
            // CrmInteractionSummary.summary is NOT NULL, and a DNP has no note
            // of its own — the topic stands in, the same fallback
            // NestedFieldWidget uses when a template renders nothing.
            summary: isConnected ? row.summary : row.summary || row.topic,
            structured_data: isConnected ? row.structured_data || null : null
          })),
        // '' / 'yes' are the widget's unanswered sentinels; the API helper
        // persists only a real date.
        follow_up_date: values.follow_up_date
      })
      showToast('Interaction logged')
      // The mark rides the Save: sync it only when the box changed state.
      if (declined.enabled && declinedChecked !== declined.active) {
        try {
          const res = await saveDeclinedMark(parentUuid, declinedChecked)
          setDeclined((d) => ({
            ...d,
            active: Boolean(res?.active),
            expires_on: res?.expires_on || null
          }))
          showToast(
            res?.active
              ? `Reminders paused for this parent until ${dayjs(
                  res.expires_on
                ).format('D MMM')}`
              : 'Reminders will run again for this parent'
          )
        } catch (error) {
          showToast(
            'The note saved, but the Declined-subscription mark did not — reopen and try again',
            'error'
          )
        }
      }
      resetForm({ values: initialValues })
      await loadHistory(parentUuid)
      if (onLogged) onLogged()
    } catch (error) {
      showToast('Could not log the interaction', 'error')
    }
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.drawerPaper }}
    >
      <div className={classes.header}>
        <Box>
          <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
            {parent?.name || 'Parent'}
          </Typography>
          {parent?.phone ? (
            <Typography variant="caption" color="textSecondary">
              {parent.phone}
            </Typography>
          ) : null}
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {/* Log a new interaction */}
      <div className={classes.section}>
        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
          Log interaction
        </Typography>
        {/* Keyed on the parent so switching rows remounts with a clean form
            rather than carrying the previous family's answers across. */}
        <Formik
          key={parentUuid || 'none'}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {({ submitForm, isSubmitting }) => (
            <div className={classes.form}>
              {/* Topic shows on every outcome, DNP included — see
                  InteractionFields. */}
              <InteractionFields />
              {declined.enabled ? (
                <Box
                  style={{
                    padding: '8px 10px',
                    border: `1px solid ${
                      declinedChecked ? '#2E7D5B' : '#e0e0e0'
                    }`,
                    borderRadius: 8,
                    background: declinedChecked ? '#f0f7f3' : '#fafafa',
                    display: 'flex',
                    alignItems: 'flex-start'
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={declinedChecked}
                    onChange={(e) => setDeclinedChecked(e.target.checked)}
                    style={{ padding: 4 }}
                    disabled={isSubmitting}
                  />
                  <Box style={{ paddingTop: 3 }}>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      Parent has declined a subscription
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {declined.active && declined.expires_on
                        ? `Reminders are paused for this parent · resumes ${dayjs(
                            declined.expires_on
                          ).format(
                            'D MMM'
                          )}. Untick and save to start reminding again.`
                        : `Attends individual paid sessions only — tick only after the parent has said so. Saving pauses the daily reminders for ${
                            declined.days || 60
                          } days; HQ keeps seeing them on the audit board.`}
                    </Typography>
                  </Box>
                </Box>
              ) : null}
              <Button
                style={{ textTransform: 'none', alignSelf: 'flex-start' }}
                variant="contained"
                color="primary"
                disableElevation
                disabled={isSubmitting}
                onClick={submitForm}
              >
                {isSubmitting ? (
                  <CircularProgress size={18} />
                ) : (
                  'Log interaction'
                )}
              </Button>
            </div>
          )}
        </Formik>
      </div>

      <Divider />

      {/* Past interactions */}
      <div className={classes.section} style={{ overflowY: 'auto' }}>
        <Typography
          variant="subtitle2"
          style={{ fontWeight: 700, marginBottom: 4 }}
        >
          History
        </Typography>
        {loadingHistory ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={22} />
          </Box>
        ) : history.length === 0 ? (
          <Typography variant="body2" className={classes.muted}>
            No interactions logged yet.
          </Typography>
        ) : (
          history.map((h) => (
            <div key={h.uuid} className={classes.histItem}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="baseline"
              >
                <Typography variant="caption" color="textSecondary">
                  {dayMonthYear(h.interaction_date)}
                  {h.interaction_mode ? ` · ${h.interaction_mode}` : ''}
                  {h.connected_status && h.connected_status !== 'Connected'
                    ? ` · ${h.connected_status}`
                    : ''}
                </Typography>
                {h.created_by?.full_name ? (
                  <Typography variant="caption" className={classes.muted}>
                    {h.created_by.full_name}
                  </Typography>
                ) : null}
              </Box>
              <Box mt={0.5}>
                {(h.interaction_topics || []).map((it, j) => (
                  <React.Fragment key={j}>
                    <span className={classes.topicChip}>{it.topic}</span>
                    <div
                      className={classes.summaryHtml}
                      dangerouslySetInnerHTML={{ __html: it.summary }}
                    />
                  </React.Fragment>
                ))}
                {(h.interaction_topics || []).length === 0 &&
                h.connected_status !== 'Connected' ? (
                  <Chip
                    size="small"
                    label={h.connected_status || 'Not connected'}
                  />
                ) : null}
              </Box>
            </div>
          ))
        )}
      </div>
    </Drawer>
  )
}

export default InteractionDrawer
